import React, { useState } from 'react';
import { Download, BookOpen, Archive, Settings2, X, Check, FileText, Heart, Star, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ExportPDFModal Component
 * 
 * Two preset editions:
 * - Keepsake Edition: Written wishes & starred moments (for printing)
 * - Complete Archive: Everything including hearts
 * 
 * Plus custom filter options
 */
export function ExportPDFModal({ 
  isOpen, 
  onClose, 
  eventId, 
  eventName,
  token,
  stats
}) {
  const [selectedEdition, setSelectedEdition] = useState('heirloom');
  const [showCustomOptions, setShowCustomOptions] = useState(false);
  const [customOptions, setCustomOptions] = useState({
    include_written_wishes: true,
    include_starred_only: false,
    include_table_groupings: true,
    include_hearts: false
  });
  const [coupleNames, setCoupleNames] = useState('');
  const [generating, setGenerating] = useState(false);

  const handleExport = async (edition = selectedEdition) => {
    setGenerating(true);
    
    try {
      // Determine config based on edition or custom
      let config;
      if (edition === 'custom') {
        config = {
          edition: 'custom',
          ...customOptions,
          couple_names: coupleNames || null
        };
      } else if (edition === 'heirloom') {
        config = {
          edition: 'heirloom',
          include_written_wishes: true,
          include_starred_only: false,
          include_table_groupings: true,
          include_hearts: false,
          couple_names: coupleNames || null
        };
      } else {
        config = {
          edition: 'complete',
          include_written_wishes: true,
          include_starred_only: false,
          include_table_groupings: true,
          include_hearts: true,
          couple_names: coupleNames || null
        };
      }

      const response = await axios.post(
        `${API}/events/${eventId}/wishes/export-pdf`,
        config,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const safeName = (coupleNames || eventName || 'wedding-guestbook')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
      link.download = `${safeName}-guestbook-${edition}.pdf`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
      onClose();
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-slate-900 border-slate-800">
        <DialogHeader>
          <DialogTitle className="text-xl font-serif text-white flex items-center gap-2">
            <Download className="h-5 w-5 text-amber-400" />
            Download Your Love Gallery
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {/* Couple Names Input */}
          <div className="space-y-2">
            <label className="text-sm text-slate-400">
              Couple's Names (for PDF cover)
            </label>
            <input
              type="text"
              value={coupleNames}
              onChange={(e) => setCoupleNames(e.target.value)}
              placeholder={eventName || "Rafael & Ilona"}
              className="w-full px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          {/* Edition Selection */}
          <div className="space-y-3">
            {/* Keepsake Edition */}
            <button
              onClick={() => setSelectedEdition('heirloom')}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                selectedEdition === 'heirloom' 
                  ? 'border-amber-500 bg-amber-500/10' 
                  : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${selectedEdition === 'heirloom' ? 'bg-amber-500/20' : 'bg-slate-700'}`}>
                  <BookOpen className={`h-5 w-5 ${selectedEdition === 'heirloom' ? 'text-amber-400' : 'text-slate-400'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white">Keepsake Edition</h3>
                    {selectedEdition === 'heirloom' && <Check className="h-4 w-4 text-amber-400" />}
                  </div>
                  <p className="text-sm text-slate-400 mt-1">
                    Written wishes & starred moments
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Perfect for printing as a keepsake book
                  </p>
                </div>
              </div>
            </button>

            {/* Complete Archive */}
            <button
              onClick={() => setSelectedEdition('complete')}
              className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                selectedEdition === 'complete' 
                  ? 'border-blue-500 bg-blue-500/10' 
                  : 'border-slate-700 hover:border-slate-600 bg-slate-800/50'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${selectedEdition === 'complete' ? 'bg-blue-500/20' : 'bg-slate-700'}`}>
                  <Archive className={`h-5 w-5 ${selectedEdition === 'complete' ? 'text-blue-400' : 'text-slate-400'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white">Complete Archive</h3>
                    {selectedEdition === 'complete' && <Check className="h-4 w-4 text-blue-400" />}
                  </div>
                  <p className="text-sm text-slate-400 mt-1">
                    Every wish, heart & moment
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Your complete digital memory
                  </p>
                </div>
              </div>
            </button>
          </div>

          {/* Custom Options Toggle */}
          <button
            onClick={() => setShowCustomOptions(!showCustomOptions)}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <Settings2 className="h-4 w-4" />
            {showCustomOptions ? 'Hide' : 'Show'} Custom Options
          </button>

          {/* Custom Options */}
          {showCustomOptions && (
            <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 space-y-3">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-white">
                  <FileText className="h-4 w-4 text-blue-400" />
                  Include Written Wishes
                </label>
                <input
                  type="checkbox"
                  checked={customOptions.include_written_wishes}
                  onChange={(e) => setCustomOptions(prev => ({ ...prev, include_written_wishes: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-amber-500 focus:ring-amber-500"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-white">
                  <Star className="h-4 w-4 text-yellow-400" />
                  Starred Only (Highlights)
                </label>
                <input
                  type="checkbox"
                  checked={customOptions.include_starred_only}
                  onChange={(e) => setCustomOptions(prev => ({ ...prev, include_starred_only: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-amber-500 focus:ring-amber-500"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-white">
                  <Users className="h-4 w-4 text-green-400" />
                  Include Table Groupings
                </label>
                <input
                  type="checkbox"
                  checked={customOptions.include_table_groupings}
                  onChange={(e) => setCustomOptions(prev => ({ ...prev, include_table_groupings: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-amber-500 focus:ring-amber-500"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-white">
                  <Heart className="h-4 w-4 text-pink-400" />
                  Include Hearts (Quick Toasts)
                </label>
                <input
                  type="checkbox"
                  checked={customOptions.include_hearts}
                  onChange={(e) => setCustomOptions(prev => ({ ...prev, include_hearts: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-amber-500 focus:ring-amber-500"
                />
              </div>
            </div>
          )}

          {/* Stats Preview */}
          {stats && (
            <div className="flex items-center justify-center gap-6 py-3 text-center border-t border-slate-800">
              <div>
                <p className="text-lg font-bold text-pink-400">{stats.total_hearts}</p>
                <p className="text-xs text-slate-500">hearts</p>
              </div>
              <div>
                <p className="text-lg font-bold text-blue-400">{stats.total_messages}</p>
                <p className="text-xs text-slate-500">wishes</p>
              </div>
              <div>
                <p className="text-lg font-bold text-yellow-400">{stats.starred_count || 0}</p>
                <p className="text-xs text-slate-500">starred</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-slate-700"
              disabled={generating}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleExport(showCustomOptions ? 'custom' : selectedEdition)}
              disabled={generating}
              className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-900"
            >
              {generating ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                  Generating...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Download PDF
                </div>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ExportPDFModal;
